import React, { useEffect, useMemo, useState } from "react"
import { Box, MenuItem } from "@material-ui/core"
import { mapToIds, multipleSelectChange, toId, useFormUtils, removeTypenames } from "../../utils"
import { CreatorActions } from "../Creators"
import { LocationOutlinedSelect, OutlinedSelect, TemplateOutlinedSelect } from "../OutlinedSelect"
import { EVENT_NAME, TRIGGER_TARGET_TYPE } from "../../data"
import { UsersGroupsAssigner } from "../Assigners"
import { useAuth } from "../../services"
import { TextDivider } from "../TextDivider"
import { formatEventLabel } from "../../utils/events"
import {
  useMutationCreateEventTrigger,
  useMutationUpdateEventTrigger,
} from "../../data/triggers/useMutationEventTrigger"
import TriggerTargetTypeOutlinedSelect from "./TriggerTargetTypeOutlinedSelect"
import TagsTextField from "../TextField/TagsTextField"
import TriggerNotifyMethodOutlinedSelect from "./TriggerNotifyMethodOutlinedSelect"

const { NOTIFY_USERS, NOTIFY_EMAILS, INTEGRATION } = TRIGGER_TARGET_TYPE

const initial = {
  eventSource: {
    events: [],
    locations: [],
    process: "",
    processStep: "",
  },
  targetType: "",
  notifyUsersTarget: {
    methods: [],
    users: [],
    groups: [],
  },
  notifyEmailsTarget: {
    emails: [],
  },
  integrationTarget: "",
}

const EventSourceTrigger = ({ edit, onClose, cy }) => {
  const [createTrigger, { loading: createLoading }] = useMutationCreateEventTrigger()
  const [updateTrigger, { loading: updateLoading }] = useMutationUpdateEventTrigger()
  const {
    settings: { locations },
  } = useAuth()
  const { isValid } = useFormUtils()
  const [eventSource, setEventSource] = useState(initial.eventSource)
  const [targetType, setTargetType] = useState("")
  const [notifyUsersTarget, setNotifyUsersTarget] = useState(initial.notifyUsersTarget)
  const [notifyEmailsTarget, setNotifyEmailsTarget] = useState(initial.notifyEmailsTarget)
  const [integrationTarget, setIntegrationTarget] = useState("")

  const [initialPicked, setInitialPicked] = useState(null)

  useEffect(() => {
    if (edit) {
      setEventSource(() => ({
        ...removeTypenames(edit.eventSource),
        locations: !edit.eventSource?.locations?.length ? ["all"] : mapToIds(edit.eventSource.locations),
        process: toId(edit.eventSource.process, true),
        processStep: toId(edit.eventSource.processStep, true),
      }))
      setTargetType(edit.targetType)
      if (edit.targetType === NOTIFY_USERS) {
        setNotifyUsersTarget(() => ({
          ...removeTypenames(edit.notifyUsersTarget),
          users: mapToIds(edit.notifyUsersTarget.users),
          groups: mapToIds(edit.notifyUsersTarget.groups),
        }))
      }
      if (edit.targetType === NOTIFY_EMAILS) {
        setNotifyEmailsTarget(removeTypenames(edit.notifyEmailsTarget))
      }
      setIntegrationTarget(edit.integrationTarget)

      if (edit.targetType === NOTIFY_USERS) {
        setInitialPicked([...edit.notifyUsersTarget.users, ...edit.notifyUsersTarget.groups])
      }
    } else {
      setInitialPicked([])
    }
  }, [edit])

  const handleTargetTypeChange = (event) => {
    setTargetType(event.target.value)
  }

  const handleLocationsChange = (event) => {
    setEventSource((prev) => ({
      ...prev,
      locations: [...multipleSelectChange(eventSource.locations, event)],
    }))
  }

  const handleRegionChange = (newLocations) => {
    setEventSource((prev) => ({
      ...prev,
      locations: [...mapToIds(newLocations)],
    }))
  }

  const handleEventsChange = (event) => {
    setEventSource((prev) => ({
      ...prev,
      events: [...multipleSelectChange(eventSource.events, event)],
    }))
  }

  const handleTemplateChange = (event) => {
    setEventSource((prev) => ({
      ...prev,
      process: event.target.value,
    }))
  }

  const handleNotifyMethodsChange = (event) => {
    setNotifyUsersTarget((prev) => ({
      ...prev,
      methods: [...multipleSelectChange(notifyUsersTarget.methods, event)],
    }))
  }

  const handleNotifyUsersPickedChange = (picked) => {
    const newHasEveryone = picked.find((p) => p.__typename === "Everyone")
    setNotifyUsersTarget((prev) => ({
      ...prev,
      users: newHasEveryone ? [] : [...mapToIds(picked.filter((p) => p.__typename === "User"))],
      groups: newHasEveryone ? [] : [...mapToIds(picked.filter((p) => p.__typename === "Group"))],
    }))
  }

  const handleEmailsChange = (event, options) => {
    setNotifyEmailsTarget((prev) => ({
      ...prev,
      emails: options,
    }))
  }

  const handleSubmit = async () => {
    const variables = {
      input: {
        eventSource: {
          ...eventSource,
          locations: eventSource.locations.includes("all") ? [] : eventSource.locations,
          process: toId(eventSource.process, true) || null,
          processStep: toId(eventSource.processStep, true) || null,
        },
        targetType,
      },
    }

    if (!eventSource.process) {
      delete variables.input.eventSource.process
    }
    if (!eventSource.processStep) {
      delete variables.input.eventSource.processStep
    }

    switch (targetType) {
      case NOTIFY_USERS:
        variables.input.notifyUsersTarget = { ...notifyUsersTarget }
        break
      case NOTIFY_EMAILS:
        variables.input.notifyEmailsTarget = { ...notifyEmailsTarget }
        break
      case INTEGRATION:
        variables.input.integrationTarget = { ...integrationTarget }
        break
      default:
        throw Error("No target type selected")
    }

    if (edit) {
      await updateTrigger({
        variables: {
          id: toId(edit),
          ...variables,
        },
      })
    } else {
      await createTrigger({
        variables,
      })
    }

    onClose && onClose()
  }

  const handleClose = () => {
    onClose && onClose()
  }

  const formValid = useMemo(
    () =>
      Boolean(eventSource?.events?.length) &&
      Boolean(eventSource?.locations?.length) &&
      isValid(targetType) &&
      (targetType !== NOTIFY_USERS ||
        Boolean(
          notifyUsersTarget?.methods?.length && (notifyUsersTarget?.users?.length || notifyUsersTarget?.groups?.length)
        )) &&
      (targetType !== NOTIFY_EMAILS || notifyEmailsTarget?.emails?.length),
    [eventSource, isValid, notifyEmailsTarget, notifyUsersTarget, targetType]
  )

  const hasActionEvent = eventSource.events.some((item) => item.startsWith("action_"))
  const hasJobEvent = eventSource.events.some((item) => item.startsWith("job_"))
  // const hasJobStepEvent = eventSource.events.some((item) => item.startsWith("job_step_"))
  const hasNotification = targetType.startsWith("notify_")

  const loading = createLoading || updateLoading

  return (
    <>
      <Box mb={1}>
        <Box mb={2}>
          <OutlinedSelect
            value={eventSource.events}
            label="Which event(s) should this trigger respond to?"
            native={false}
            onChange={handleEventsChange}
            multiple
            required
          >
            {Object.values(EVENT_NAME)
              .filter((item) => [EVENT_NAME.JOB_COMPLETED, EVENT_NAME.JOB_OVERDUE].includes(item))
              .map((value) => (
                <MenuItem key={value} value={value}>
                  {formatEventLabel(value)}
                </MenuItem>
              ))}
          </OutlinedSelect>
        </Box>

        {(hasJobEvent || hasActionEvent) && (
          <>
            <TextDivider mb={2} />

            <Box mb={2}>
              <TemplateOutlinedSelect
                label="Select job template"
                hasAll
                value={toId(eventSource?.process, true) || ""}
                onChange={handleTemplateChange}
                required={!hasActionEvent}
              />
            </Box>
          </>
        )}

        <TextDivider mb={2} />

        <Box mb={2}>
          <TriggerTargetTypeOutlinedSelect value={targetType} onChange={handleTargetTypeChange} required />
        </Box>

        {hasNotification && (
          <>
            {targetType === NOTIFY_USERS && (
              <Box mb={2}>
                <TriggerNotifyMethodOutlinedSelect
                  value={notifyUsersTarget.methods}
                  onChange={handleNotifyMethodsChange}
                  multiple
                />
              </Box>
            )}
            <Box mb={2}>
              <LocationOutlinedSelect
                label="For which locations?"
                value={eventSource?.locations || []}
                onChange={handleLocationsChange}
                onRegionChange={handleRegionChange}
                multiple
              />
            </Box>
          </>
        )}

        {targetType === NOTIFY_USERS && Boolean(initialPicked) && (
          <UsersGroupsAssigner
            title="Who should be notified?"
            allowSelf
            allowAll={false}
            locations={mapToIds(locations)}
            initialPicked={initialPicked}
            onPickedChanged={handleNotifyUsersPickedChange}
            cy="TriggerCreator-notification-to"
          />
        )}

        {targetType === NOTIFY_EMAILS && (
          <Box my={2}>
            <TagsTextField
              value={notifyEmailsTarget.emails || []}
              label="Email Address(es)"
              placeholder="Type address and press enter..."
              inputProps={{ type: "email" }}
              onChange={handleEmailsChange}
            />
          </Box>
        )}
      </Box>

      <CreatorActions
        id={`${cy}-CreatorActions`}
        subject="Trigger"
        onClose={handleClose}
        onSubmit={handleSubmit}
        submitLoading={loading}
        disableSubmit={!formValid}
      />
    </>
  )
}

export default EventSourceTrigger
